import { useRef, useEffect } from "react";
import { cn } from "./cn";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  position?: "bottom" | "right" | "left" | "top"; 
  isBlur?: boolean;
}

const Drawer: React.FC<DrawerProps> = ({ 
  isOpen, 
  onClose, 
  children, 
  className,
  position = "bottom" ,
  isBlur = false
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const positionClasses = {
    bottom: "items-end justify-center ",
    top: "items-start justify-center ",
    left: "items-center justify-start ",
    right: "items-center justify-end "
  };

  return (
    <div className={`fixed inset-0 z-50 flex bg-black bg-opacity-50 ${isBlur ? "backdrop-blur-lg" : ""} transition-opacity duration-300 ${positionClasses[position]}`}
    >
      <div
      ref={drawerRef}
      style={{
      borderRadius: position === "bottom" ? "1rem 1rem 0 0" : position === "top" ? "0 0 1rem 1rem" : "0",
      }}
      className={cn(
      "bg-transparent w-full max-w-sm max-h-[90vh] overflow-y-auto transform transition-transform duration-300",
      position === "bottom" && "animate-slide-up",
      position === "top" && "animate-slide-down",
      position === "left" && "animate-slide-right h-full",
      position === "right" && "animate-slide-left h-full",
      className
      )}
      >
      {children}
      </div>
    </div>
  );
};

export default Drawer;